import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createStore, createHook, type Action } from '@atlassian/react-sweet-state';
import {
	addYears,
	isDateRangeMoreThanYearApart,
	isStartDateAfterEndDate,
	roundBackwardToExcludeFutureDates,
	roundForwardToExcludeBadDeploymentsData,
	subDays,
} from '../../common/utils/dates';

const PAST_TWO_WEEKS = '-2w' as const;
const PAST_MONTH = '-1m' as const;
const PAST_QUARTER = '-3m' as const;
const PAST_YEAR = '-1y' as const;
const NOW = 'now' as const;

export const RelativeDateFilterOptions = {
	PAST_TWO_WEEKS,
	PAST_MONTH,
	PAST_QUARTER,
	PAST_YEAR,
	NOW,
} as const;

export type RelativeDateFilterOption =
	(typeof RelativeDateFilterOptions)[keyof typeof RelativeDateFilterOptions];

export type RelativeDateRange = {
	startDate: RelativeDateFilterOption;
	endDate: RelativeDateFilterOption;
};

export type RelativeDateFilter = {
	type: 'RELATIVE';
} & RelativeDateRange;

export type CustomDateRange = {
	startDate: Date;
	endDate: Date;
};
export type CustomDateFilter = {
	type: 'CUSTOM';
} & CustomDateRange;

export type DateFilter = RelativeDateFilter | CustomDateFilter;
export type State = {
	dateFilter: DateFilter;
};

export const defaultDateFilter = {
	type: 'RELATIVE',
	startDate: RelativeDateFilterOptions.PAST_QUARTER,
	endDate: RelativeDateFilterOptions.NOW,
} as const;

export const initialState: State = {
	dateFilter: defaultDateFilter,
};

export const getValidatedDateRange = ({
	startDate,
	endDate,
}: {
	startDate: Date;
	endDate: Date;
}): {
	startDate: Date;
	endDate: Date;
	errorType: 'START-AFTER-END' | 'MORE-THAN-YEAR-APART' | undefined;
} => {
	let finalStartDate = startDate;
	let finalEndDate = endDate;
	let errorType;

	// start date after end date
	if (isStartDateAfterEndDate(finalStartDate, finalEndDate)) {
		finalEndDate = finalStartDate;
		errorType = 'START-AFTER-END';
	}

	// end date is future date
	finalEndDate = roundBackwardToExcludeFutureDates(finalEndDate);

	// start date is before bad deployments data date
	finalStartDate = roundForwardToExcludeBadDeploymentsData(finalStartDate);

	// date range greater than a year
	if (isDateRangeMoreThanYearApart(finalStartDate, finalEndDate)) {
		finalEndDate = subDays(addYears(finalStartDate, 1), 1);
		errorType = 'MORE-THAN-YEAR-APART';
	}

	return {
		startDate: finalStartDate,
		endDate: finalEndDate,
		// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type '"START-AFTER-END" | "MORE-THAN-YEAR-APART" | undefined'.
		errorType,
	};
};

export const actions = {
	setDateFilter:
		({ type, startDate, endDate }: DateFilter): Action<State> =>
		({ dispatch }) => {
			if (type === 'RELATIVE') {
				dispatch(actions.setRelativeDateFilter(startDate));
			} else if (type === 'CUSTOM') {
				dispatch(
					actions.setCustomDateFilter({
						startDate,
						endDate,
					}),
				);
			} else {
				throw new Error('unrecognised date filter type');
			}
		},
	resetFilter:
		(): Action<State> =>
		({ setState }) =>
			setState(initialState),
	setRelativeDateFilter:
		(relativeDateFilter: RelativeDateFilterOption): Action<State> =>
		({ setState }) =>
			setState({
				dateFilter: {
					type: 'RELATIVE',
					startDate: relativeDateFilter,
					endDate: RelativeDateFilterOptions.NOW,
				},
			}),
	setCustomDateFilter:
		(customDateRange: CustomDateRange): Action<State> =>
		({ setState, dispatch }) => {
			const { startDate, endDate, errorType } = getValidatedDateRange(customDateRange);

			if (errorType) {
				// a user will end up here if they enter a query string that doesn't satisfy the
				// requirements for a date filter. e.g. more than a year in time span.

				fireErrorAnalytics({
					meta: {
						id: 'invalidDateFilter',
						packageName: 'jiraSoftwareDeployments',
						teamName: 'fusion-solaris',
					},
					attributes: {
						// @ts-expect-error - TS2322 - Type 'Date' is not assignable to type 'string | number | boolean | AnalyticsAttributes | null | undefined'.
						startDate,
						// @ts-expect-error - TS2322 - Type 'Date' is not assignable to type 'string | number | boolean | AnalyticsAttributes | null | undefined'.
						endDate,
					},
				});

				dispatch(actions.resetFilter());
			} else {
				setState({
					dateFilter: {
						type: 'CUSTOM',
						startDate,
						endDate,
					},
				});
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'useDateFilterStore',
	initialState,
	actions,
});

export const useDateFilterStore = createHook(Store);

export const useDateFilter = createHook(Store, {
	selector: (state) => state.dateFilter,
});
